import { useMemo, useRef, useState, useLayoutEffect } from 'react'
import type { FC } from 'react'
import Plot from 'react-plotly.js'
import UpSetJSImpl, { extractCombinations } from '@upsetjs/react'
import type { SearchAnalyticsResponse } from '../api/search'

// `@upsetjs/react`'s default export is a forwardRef whose ReactElement
// return type isn't assignable to React 19's stricter JSX node type.
// Cast once so the JSX site stays clean.
const UpSetJS = UpSetJSImpl as unknown as FC<Record<string, unknown>>

interface Props {
  data: SearchAnalyticsResponse
  excludedKeywords?: string[]
  onExcludeChange?: (excluded: string[]) => void
}

type CombinationMode = 'intersection' | 'distinctIntersection'

const TOP_N_OPTIONS = [5, 10, 15, 0] as const // 0 == all
const MAX_COMBINATIONS = 40

export default function SearchAnalytics({ data, excludedKeywords = [], onExcludeChange }: Props) {
  const [topNSets, setTopNSets] = useState<number>(10)
  const [mode, setMode] = useState<CombinationMode>('intersection')
  const upsetContainerRef = useRef<HTMLDivElement>(null)
  const [upsetWidth, setUpsetWidth] = useState(900)

  useLayoutEffect(() => {
    const el = upsetContainerRef.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) {
        setUpsetWidth(Math.max(400, Math.floor(entry.contentRect.width)))
      }
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // --- Keyword exclusion checkboxes ---
  const keywordExclusionUI = (
    <div className="bg-white rounded-lg border p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Include Keywords in Analysis</h3>
      <p className="text-xs text-gray-500 mb-3">
        Uncheck broad keywords (e.g., &quot;Alzheimer&quot;) to focus the extracted-value upset on specific overlaps.
      </p>
      <div className="flex flex-wrap gap-3">
        {data.all_keywords.map(kw => {
          const isIncluded = !excludedKeywords.includes(kw)
          return (
            <label
              key={kw}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 rounded border cursor-pointer hover:bg-gray-100"
            >
              <input
                type="checkbox"
                checked={isIncluded}
                onChange={() => {
                  if (!onExcludeChange) return
                  if (isIncluded) {
                    onExcludeChange([...excludedKeywords, kw])
                  } else {
                    onExcludeChange(excludedKeywords.filter(k => k !== kw))
                  }
                }}
                className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
              />
              <span className="text-xs text-gray-700">{kw}</span>
              <span className="text-[10px] text-gray-400">({data.keyword_counts[kw]})</span>
            </label>
          )
        })}
      </div>
    </div>
  )

  // --- Trend plot: publications per year ---
  const years = Object.keys(data.year_counts).map(Number).sort((a, b) => a - b)
  const yearValues = years.map(y => data.year_counts[String(y)])

  const trendPlot = (
    <Plot
      data={[
        {
          x: years.map(String),
          y: yearValues,
          type: 'bar',
          marker: { color: '#3498db', opacity: 0.8 },
          name: 'Publications',
        },
      ]}
      layout={{
        title: { text: 'Publications per Year', font: { size: 16 } },
        xaxis: { title: 'Year', tickangle: -45 },
        yaxis: { title: 'Number of Publications' },
        margin: { t: 40, b: 60, l: 50, r: 20 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        autosize: true,
      }}
      config={{ responsive: true, displayModeBar: false }}
      style={{ width: '100%', height: '350px' }}
    />
  )

  // --- UpSet plot ---
  // Restrict input to the top-N most frequent extracted values so the matrix
  // stays readable; topNSets===0 means "all" (capped by what the backend ships).
  const topSets = useMemo(() => {
    const counts = data.extracted_counts
    const ranked = Object.keys(counts).sort((a, b) => counts[b] - counts[a])
    return topNSets > 0 ? new Set(ranked.slice(0, topNSets)) : new Set(ranked)
  }, [data.extracted_counts, topNSets])

  const upsetElems = useMemo(() => {
    return data.article_sets
      .map(a => ({ name: a.pmcid, sets: a.sets.filter(s => topSets.has(s)) }))
      .filter(e => e.sets.length > 0)
  }, [data.article_sets, topSets])

  const { sets, combinations } = useMemo(() => {
    if (upsetElems.length === 0) return { sets: [], combinations: [] }
    return extractCombinations(upsetElems, {
      type: mode,
      combinationLimit: MAX_COMBINATIONS,
      combinationOrder: ['cardinality:desc', 'name:asc'],
    })
  }, [upsetElems, mode])

  const upsetPlot = (
    <div ref={upsetContainerRef} className="bg-white rounded-lg border p-4">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Extracted Value Intersections</h3>
          <p className="text-xs text-gray-500 mt-1">
            How extracted values (e.g., specific microRNAs) co-occur across articles.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <label className="flex items-center gap-1.5">
            <span className="text-gray-600">Sets:</span>
            <select
              value={topNSets}
              onChange={e => setTopNSets(Number(e.target.value))}
              className="px-2 py-1 border rounded"
            >
              {TOP_N_OPTIONS.map(n => (
                <option key={n} value={n}>{n === 0 ? 'All' : `Top ${n}`}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1.5">
            <span className="text-gray-600">Type:</span>
            <select
              value={mode}
              onChange={e => setMode(e.target.value as CombinationMode)}
              className="px-2 py-1 border rounded"
            >
              <option value="intersection">Inclusive (these + possibly others)</option>
              <option value="distinctIntersection">Distinct (exactly these)</option>
            </select>
          </label>
        </div>
      </div>
      {sets.length === 0 ? (
        <div className="text-center text-gray-400 py-8">No intersections available.</div>
      ) : (
        <UpSetJS
          sets={sets}
          combinations={combinations}
          width={upsetWidth}
          height={Math.max(360, 36 * sets.length + 220)}
          theme="light"
          fontSizes={{ axisTick: '10px', setLabel: '12px', barLabel: '11px' }}
        />
      )}
    </div>
  )

  const hasExtracted = data.article_sets.length > 0

  return (
    <div className="space-y-6">
      {/* Stats summary */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{data.total_articles.toLocaleString()}</div>
          <div className="text-sm text-gray-600">Unique Articles</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{data.total_matches.toLocaleString()}</div>
          <div className="text-sm text-gray-600">Total Matches</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">{Object.keys(data.keyword_counts).length}</div>
          <div className="text-sm text-gray-600">Keywords</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">{Object.keys(data.extracted_counts).length}</div>
          <div className="text-sm text-gray-600">Extracted Values</div>
        </div>
      </div>

      {/* Trend plot */}
      <div className="bg-white rounded-lg border p-4">
        {trendPlot}
      </div>

      {/* Keyword exclusion checkboxes — above upset so user can filter before viewing */}
      {data.all_keywords.length > 0 && keywordExclusionUI}

      {/* Real UpSet plot */}
      {hasExtracted && upsetPlot}
    </div>
  )
}
